import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Loader2, Printer, ArrowLeft, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoicePage = () => {
  const { transactionId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payments/invoice/${transactionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to load invoice');
        }
        const data = await response.json();
        setInvoice(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [transactionId, token]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="p-6 max-w-3xl mx-auto text-center space-y-4">
          <p className="text-red-600 font-medium">{error}</p>
          <Button variant="outline" onClick={() => navigate('/billing')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Billing
          </Button>
        </div>
      </Layout>
    );
  }

  const inv = invoice;
  const invoiceDate = new Date(inv.invoice_date);
  const cycleLabel = inv.items[0]?.billing_cycle === 'yearly' ? 'Year' : 'Month';

  return (
    <Layout>
      <div className="p-4 max-w-4xl mx-auto" data-testid="invoice-page">
        {/* Action Bar - hidden on print */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Button variant="ghost" onClick={() => navigate('/billing')} data-testid="back-to-billing-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Billing
          </Button>
          <div className="flex gap-2">
            <Button onClick={handlePrint} data-testid="print-invoice-btn">
              <Printer className="w-4 h-4 mr-2" /> Print / Save PDF
            </Button>
          </div>
        </div>

        {/* Invoice Document */}
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm p-8 print:shadow-none print:border-none print:p-0" data-testid="invoice-document">
          {/* Header */}
          <div className="flex justify-between items-start mb-8 pb-6 border-b border-slate-200 dark:border-slate-700">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">TAX INVOICE</h1>
              <p className="text-sm text-slate-500 mt-1">Invoice #: <span className="font-semibold text-slate-700 dark:text-slate-200" data-testid="invoice-number">{inv.invoice_number}</span></p>
              <p className="text-sm text-slate-500">Date: <span className="font-medium text-slate-700 dark:text-slate-200">{invoiceDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></p>
            </div>
            <div className="text-right flex items-start gap-3">
              <div>
                <h2 className="text-xl font-bold text-blue-600">Inspiro Tracker</h2>
                <p className="text-xs text-slate-500 mt-0.5">Time Management</p>
                <p className="text-[10px] text-slate-400">by INPW Technologies Pvt Ltd</p>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${
                  inv.payment.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                }`} data-testid="invoice-status">
                  {inv.payment.status === 'paid' ? 'PAID' : inv.payment.status.toUpperCase()}
                </span>
              </div>
              <img src="/logo192.png" alt="Inspiro Tracker" className="w-14 h-14 rounded-xl shadow-sm" />
            </div>
          </div>

          {/* Seller & Buyer */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">From</h3>
              <div className="text-sm space-y-0.5">
                <p className="font-semibold text-slate-900 dark:text-white">{inv.seller.name}</p>
                <p className="text-slate-600 dark:text-slate-300">{inv.seller.address}</p>
                {inv.seller.gst_number && <p className="text-slate-600 dark:text-slate-300">GSTIN: {inv.seller.gst_number}</p>}
                <p className="text-slate-600 dark:text-slate-300">{inv.seller.email}</p>
              </div>
            </div>
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Bill To</h3>
              <div className="text-sm space-y-0.5">
                <p className="font-semibold text-slate-900 dark:text-white" data-testid="buyer-name">{inv.buyer.name || 'Not provided'}</p>
                {inv.buyer.address_line1 && <p className="text-slate-600 dark:text-slate-300">{inv.buyer.address_line1}</p>}
                {inv.buyer.address_line2 && <p className="text-slate-600 dark:text-slate-300">{inv.buyer.address_line2}</p>}
                {(inv.buyer.city || inv.buyer.state || inv.buyer.pincode) && (
                  <p className="text-slate-600 dark:text-slate-300">
                    {[inv.buyer.city, inv.buyer.state, inv.buyer.pincode].filter(Boolean).join(', ')}
                  </p>
                )}
                {inv.buyer.gst_number && <p className="text-slate-600 dark:text-slate-300" data-testid="buyer-gst">GSTIN: {inv.buyer.gst_number}</p>}
                {!inv.buyer.gst_number && !inv.buyer.address_line1 && (
                  <p className="text-amber-600 text-xs mt-1 italic print:hidden">
                    Update your billing details in Workspace Settings
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="mb-6">
            <table className="w-full text-sm" data-testid="invoice-items-table">
              <thead>
                <tr className="bg-slate-50 border-y border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 font-semibold text-slate-600 dark:text-slate-300">#</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600 dark:text-slate-300">Description</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600 dark:text-slate-300">SAC</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600 dark:text-slate-300">Qty</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-600 dark:text-slate-300">Rate</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-600 dark:text-slate-300">Amount</th>
                </tr>
              </thead>
              <tbody>
                {inv.items.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100 dark:border-slate-700">
                    <td className="py-3 px-4 text-slate-600 dark:text-slate-300">{idx + 1}</td>
                    <td className="py-3 px-4 text-slate-900 dark:text-white font-medium">
                      {item.description}
                      <span className="block text-xs text-slate-500 mt-0.5">
                        {item.quantity} seat(s) @ ₹{item.unit_price}/{cycleLabel.toLowerCase()}
                        {item.has_discount && item.original_price && (
                          <> <span className="line-through text-slate-400">₹{item.original_price}</span> <span className="text-emerald-600 font-semibold">{item.discount_label} applied</span></>
                        )}
                        {item.total_users && item.total_users > item.quantity && (
                          <> (total seats after: {item.total_users})</>
                        )}
                        {item.is_prorated && (
                          <span className="block text-[10px] text-amber-600 mt-0.5">Pro-rated for remaining billing period</span>
                        )}
                      </span>
                      <span className="block text-[10px] text-slate-400 mt-0.5 italic">
                        Price inclusive of 18% GST
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-slate-600 dark:text-slate-300">{item.sac_code}</td>
                    <td className="py-3 px-4 text-center text-slate-600 dark:text-slate-300">{item.quantity}</td>
                    <td className="py-3 px-4 text-right text-slate-600 dark:text-slate-300">
                      ₹{item.unit_price.toLocaleString('en-IN')}/{cycleLabel.toLowerCase()}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-slate-900 dark:text-white">
                      ₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-72 space-y-2 text-sm">
              <div className="flex justify-between py-1">
                <span className="text-slate-500">Taxable Amount</span>
                <span className="text-slate-700 dark:text-slate-200">₹{inv.taxable_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">CGST (9%)</span>
                <span className="text-slate-700 dark:text-slate-200">₹{inv.cgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">SGST (9%)</span>
                <span className="text-slate-700 dark:text-slate-200">₹{inv.sgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between py-1 text-xs">
                <span className="text-slate-400 italic">GST inclusive in total amount</span>
              </div>
              <div className="flex justify-between py-2 border-t-2 border-slate-900 font-bold text-base">
                <span className="text-slate-900 dark:text-white">Total</span>
                <span className="text-slate-900 dark:text-white" data-testid="invoice-total">₹{inv.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6 text-sm">
            <h4 className="font-semibold text-slate-700 dark:text-slate-200 mb-2">Payment Details</h4>
            <div className="grid grid-cols-2 gap-2 text-slate-600 dark:text-slate-300">
              <div>Payment Method: <span className="font-medium text-slate-800">{inv.payment.provider === 'stripe' ? 'Stripe (Card)' : 'Razorpay (UPI/Card)'}</span></div>
              <div>Transaction ID: <span className="font-medium text-slate-800 text-xs">{inv.payment.payment_id || inv.transaction_id}</span></div>
              <div>Currency: <span className="font-medium text-slate-800">{inv.payment.currency}</span></div>
              <div>Status: <span className="font-medium text-emerald-700">Paid</span></div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-4 text-xs text-slate-400 text-center space-y-1">
            <p>This is a computer-generated invoice and does not require a physical signature.</p>
            <p>For any queries, contact accounts@inspiroworks.com</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default InvoicePage;
